<template>
  <div>
    <div class="header">
      <div class="header__item" style="margin-top: 20px">
        <CustomSelect
          :options="this.residencyOptions"
          label="ЖК"
          optionValueKey="resident_id"
          optionLabelKey="resident_name"
          :value="this.filters.resident_id"
          @change="(value) => onChange('resident_id', value)"
        />
      </div>
      <q-btn
        type="submit"
        @click="this.getData"
        :loading="this.isFetching"
        :disable="this.deleting"
        size="sm"
        no-wrap
        color="primary"
      >
        <q-icon name="search" />
        Поиск
      </q-btn>
    </div>
    <div class="tables-container">
      <div class="main-table">
        <CustomTable
          title="Конструкции по ЖК (ББ)"
          :rowsData="this.rowsData"
          :columns="this.framesListColumns"
          :loading="isFetching"
          :show-search="true"
          :custom-body="true"
          :add-btn="true"
          :on-add-click="this.handleAddClick"
        >
          <template #customBody="props">
            <q-tr @click.stop="() => this.toggleCollapse(props.row.frame_id)">
              <custom-table-cell align="center" vertical-align="center">
                {{ props.row.frame_id }}
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                {{ props.row.frame_type_name }}
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.frame_name }}
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                <Clickable
                  v-if="props.row.frame_url"
                  :link="true"
                  :href="`${API_URL}${props.row.frame_url}`"
                >
                  Посмотреть
                  <q-tooltip>Посмотреть/Скачать оригинал</q-tooltip>
                </Clickable>
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                <CustomIcon
                  @click.stop="() => this.handleUpdateClick(props.row)"
                  icon="edit"
                  green
                  small
                />
              </custom-table-cell>

              <custom-table-cell align="center">
                <q-btn
                  @click.stop="() => this.toggleCollapse(props.row.frame_id)"
                  :color="collapsedItems[props.row.frame_id] ? 'red' : 'green'"
                  size="md"
                  class="toggle-btn"
                  rounded
                  flat
                >
                  <i
                    class="fa-solid fa-angle-right"
                    :class="{ rotated: collapsedItems[props.row.frame_id] }"
                  ></i>
                </q-btn>
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                <CustomIcon
                  icon="trash"
                  :approve-mode="true"
                  :loading="!!this.deleteLoading[props.row.frame_id]"
                  :approve="() => this.onDeleteSubmit(props.row.frame_id)"
                />
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
      </div>
      <div class="params-table" v-if="selectedFrameId">
        <FrameParams
          :columns="frameParamsColumns"
          :frameRow="selectedFrame"
          :resetState="{}"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { API_URL, MODAL_NAMES } from "@/constants";

import CheckOrClose from "@/components/common/CheckOrClose";
import Clickable from "@/components/common/Clickable";
import CollapseBlock from "@/components/common/CollapseBlock";
import CustomIcon from "@/components/common/CustomIcon";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import PopupConfirm from "@/components/common/PopupConfirm";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import FrameParams from '@/views/Frames/List/FrameParams';

import { getResidentialSettingsList } from "@/views/BuildingSettings/services";
import {
  deleteFrame,
  frameParamsColumns,
  framesListColumns,
  getFramesList,
  updateFrame
} from "@/views/Frames/List/services";
import { useAppStore } from "@/services/store/app";

export default {
  name: "FramesList",
  components: {
    CustomInput,
    CustomIcon,
    CustomTableCell,
    CustomTable,
    CustomSelect,
    PopupConfirm,
    CheckOrClose,
    CollapseBlock,
    FrameParams,
    Clickable,
  },
  data() {
    return {
      residencyOptions: [],
      rowsData: [],
      framesListColumns,
      frameParamsColumns,
      filters: {
        resident_id: "",
      },
      isFetching: false,

      deleteLoading: {},
      deleting: false,
      collapsedItems: {},
      selectedFrameId: null,
    };
  },
  computed: {
    selectedFrame() {
      if (!this.selectedFrameId) return null;
      return this.rowsData.find(row => row.frame_id === this.selectedFrameId);
    },
  },
  mounted() {
    this.getResidencyOptions();
  },
  methods: {
    async getResidencyOptions() {
      const res = await getResidentialSettingsList();
      this.residencyOptions = res || [];
    },
    async onChange(key, value) {
      this.filters[key] = value;
    },
    isResidentSelected() {
      if (!this.filters.resident_id) {
        this.showErrorSnackbar("Выберите ЖК");
        return false;
      }
      return true;
    },
    async getData() {
      if (!this.isResidentSelected()) return;
      this.isFetching = true;
      const res = await getFramesList(this.filters.resident_id);
      this.isFetching = false;
      this.selectedFrameId = null;
      if (!res) return;
      this.rowsData = res;
    },

    async handleAddClick() {
      if (!this.isResidentSelected()) return;
      this.showModal(MODAL_NAMES.FRAME_UPD, {
        onSubmit: this.onAddSubmit,
        title: "Добавить конструкцию (ББ)",
        resident_id: this.filters.resident_id,
        residencyOptions: this.residencyOptions,
      })
    },
    async handleUpdateClick(row) {
      if (!this.isResidentSelected()) return;
      this.showModal(MODAL_NAMES.FRAME_UPD, {
        onSubmit: (body) => this.onUpdateSubmit(body),
        title: "Изменить конструкцию (ББ)",
        body: row,
        resident_id: this.filters.resident_id,
      })
    },
    async onAddSubmit(body) {
      const res = await updateFrame(body);
      if (!res) return;
      this.getData();
      this.showSucceedSnackbar();
      this.closeModal()
    },
    async onUpdateSubmit(body) {
      const res = await updateFrame(body);
      if (!res) return;
      this.getData();
      this.showSucceedSnackbar();
      this.closeModal()
    },
    async onDeleteSubmit(frame_id) {
      if (this.deleteLoading[frame_id]) return;
      this.deleting = true;
      this.deleteLoading = { ...this.deleteLoading, [frame_id]: true };
      const res = await deleteFrame(frame_id);
      this.deleteLoading = { ...this.deleteLoading, [frame_id]: false };
      this.deleting = false;
      if (!res) return;
      this.rowsData = this.rowsData.filter((row) => row.frame_id !== frame_id);
      this.showSucceedSnackbar();
    },
    async toggleCollapse(frame_id) {
      const isAlreadyOpen = this.collapsedItems[frame_id];

      Object.keys(this.collapsedItems).forEach(key => {
        this.collapsedItems[key] = false;
      });

      if (!isAlreadyOpen) {
        this.collapsedItems[frame_id] = true;
        this.selectedFrameId = frame_id;
      } else {
        this.selectedFrameId = null;
      }
    },
  },
  setup() {
    const {showModal, closeModal} = useAppStore()
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, showModal, closeModal };
  },
};
</script>

<style scoped lang="scss">
.header {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 25px;
  &__item {
    width: 200px;
  }
  @media (max-width: 400px) {
    &__item {
      width: 100%;
    }
  }
}

.toggle-btn {
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.toggle-btn:hover {
  background-color: #138496;
  color: white;
  transform: scale(1.1);
  cursor: pointer;
}

.toggle-btn i {
  transition: transform 0.2s ease;
}

.toggle-btn i.rotated {
  transform: rotate(90deg);
}

.tables-container {
  display: flex;
  gap: 20px;
  width: 100%;
}

.main-table {
  width: 60%;
}

.params-table {
  width: 40%;
  position: sticky;
  top: 20px;
  align-self: flex-start;
  max-height: calc(100vh - 40px);
}

@media (max-width: 1024px) {
  .tables-container {
    flex-direction: column;
  }

  .main-table,
  .params-table {
    width: 100%;
  }
}
</style>
